// ============================================================
// THE NAME PILLS - readable labels on the display (plan map-display, Track C;
// the Focus demo's "readable labels"): a dark pill with a team stripe, as
// wide as its name and its condition glyphs, pushed up until it clears the
// pills already placed, so no two names are ever drawn over each other.
// A pill moved far from its figure gets a leader line (`moved`).
//
// Pure. Zero React dependency. Screen units (the scene's viewBox).
// ============================================================

/** A pill's height, and the gap kept between two. */
export const PILL_H = 20;
const GAP = 3;
/** Give up pushing after this many tries: a crowd that tall keeps its overlap rather than climb off the screen. */
const TRIES = 20;
/** A pill moved further than this from where it wanted to be is joined to its figure by a line. */
const LEADER_AFTER = 8;

/** The width of a pill for this name and this many condition glyphs (the demo's measure). */
export const pillWidth = (name: string, glyphs: number) => name.length * 7 + 22 + glyphs * 17;

/** Where one pill wants to sit (its left edge and top) and how wide it is. */
export interface PillBox {
  id: string;
  x: number;
  y: number;
  w: number;
}

const overlaps = (a: { x: number; y: number; w: number }, b: { x: number; y: number; w: number }) =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + PILL_H + GAP && b.y < a.y + PILL_H + GAP;

/** Push one pill up until it clears every pill already placed (or the tries run out). */
function settle(p: PillBox, placed: readonly PillBox[]): PillBox {
  let at = p;
  for (let i = 0; i < TRIES; i++) {
    const hit = placed.find((q) => overlaps(at, q));
    if (!hit) break;
    at = { ...at, y: hit.y - PILL_H - GAP };
  }
  return at;
}

/** Where each pill is drawn, top first; `moved` when it was pushed far enough to need a leader line. */
/** Where one pill is drawn, how wide it is, and whether it was pushed far enough to need a leader line. */
export interface PlacedPill {
  x: number;
  y: number;
  w: number;
  moved: boolean;
}

/** Where each pill is drawn, top first. */
export function stackPills(boxes: readonly PillBox[]): Record<string, PlacedPill> {
  const placed: PillBox[] = [];
  const out: Record<string, PlacedPill> = {};
  for (const b of [...boxes].sort((p, q) => p.y - q.y)) {
    const at = settle(b, placed);
    placed.push(at);
    out[b.id] = { x: at.x, y: at.y, w: b.w, moved: b.y - at.y > LEADER_AFTER };
  }
  return out;
}
